import { message } from 'antd'
import { pick } from 'ramda'
import { paymentSources } from 'src/constants'
import { useCategoryMap } from 'src/hooks/GamesCategoryContext'
import { fetchUtil as fetch, buildQuery } from 'src/utils/fetch'

const supportedTypes = [
  'country',
  'deviceType',
  'gender',
  'filterSkipped',
  'maxAge',
  'minAge',
  'providerCountry',
  'marketplaceId',
  'userName',
  'metaverseRoomId',
  'gameId',
  'startDate',
  'statDate',
  'endDate',
  'state',
  'groupBy',
  'gameCategory',
  'userAccessType',
  'aggregateBy',
  'source',
  'mncmcc',
  'avatarName',
  'userId',
  'categoryMap',
  'convertedMap',
  'providers',
  'paymentSource',
  'sorts',
  'category',
  'retentionDepth',
  'status',
  'name',
  'sortBy',
  'sortOrder',
]

const filterParamsBySupportedTypes = (
  params: Record<string, any>
): Record<string, any> => {
  return Object.fromEntries(
    Object.entries(params).filter(([key]) => supportedTypes.includes(key))
  )
}

const getTodayDateString = () => {
  const today = new Date()
  const yyyy = today.getFullYear()
  const mm = String(today.getMonth() + 1).padStart(2, '0')
  const dd = String(today.getDate()).padStart(2, '0')
  return `${dd}/${mm}/${yyyy}`
}

export const searchPlaylistReport = async (params: Record<string, any>) => {
  const query = buildQuery(pick(['page', 'pageSize'], params))

  const filteredParams = filterParamsBySupportedTypes(params)
  try {
    const { data } = await fetch(`/report/playlist-report/search?${query}`, {
      method: 'POST',
      body: JSON.stringify(filteredParams),
    })

    if (params.buttonAction === 'Generate Pdf') {
      const response = await fetch(`/report/playlist-report/generate/pdf`, {
        method: 'POST',
        filename: `PlaylistReport_${getTodayDateString()}.pdf`,
        body: JSON.stringify(filteredParams),
        responseType: 'blob',
      })
    } else if (params.buttonAction === 'Generate Csv') {
      const response = await fetch(`/report/playlist-report/generate/csv`, {
        method: 'POST',
        filename: `PlaylistReport_${getTodayDateString()}.csv`,
        body: JSON.stringify(filteredParams),
        responseType: 'blob',
        contentType: 'text/csv',
      })
    }

    return data
  } catch (e) {
    message.error('search playlist Report error !')
  }
}

export const searchTracksReport = async (params: Record<string, any>) => {
  const query = buildQuery(pick(['page', 'pageSize'], params))
  const filteredParams = filterParamsBySupportedTypes(params)

  try {
    if (params.buttonAction === 'Generate Pdf') {
      const response = await fetch(`/report/tracks-report/generate/pdf`, {
        method: 'POST',
        body: JSON.stringify(filteredParams),
        filename: `TrackReport_${getTodayDateString()}.pdf`,
        responseType: 'blob',
      })
    } else if (params.buttonAction === 'Generate Csv') {
      const response = await fetch(`/report/tracks-report/generate/csv`, {
        method: 'POST',
        body: JSON.stringify(filteredParams),
        filename: `TrackReport_${getTodayDateString()}.csv`,
        responseType: 'blob',
        contentType: 'text/csv',
      })
    }

    const { data } = await fetch(`/report/tracks-report/search?${query}`, {
      method: 'POST',
      body: JSON.stringify(filteredParams),
    })
    return data
  } catch (e) {
    message.error('search tracks Report error !')
  }
}

export const searchStationReport = async (params: Record<string, any>) => {
  const query = buildQuery(pick(['page', 'pageSize'], params))

  const filteredParams = filterParamsBySupportedTypes(params)
  try {
    if (params.buttonAction === 'Generate Pdf') {
      const response = await fetch(`/report/station-report/generate/pdf`, {
        method: 'POST',
        body: JSON.stringify(filteredParams),
        filename: `StationReport_${getTodayDateString()}.pdf`,
        responseType: 'blob',
      })
    } else if (params.buttonAction === 'Generate Csv') {
      const response = await fetch(`/report/station-report/generate/csv`, {
        method: 'POST',
        body: JSON.stringify(filteredParams),
        filename: `StationReport_${getTodayDateString()}.csv`,
        responseType: 'blob',
        contentType: 'text/csv',
      })
    }

    const { data } = await fetch(`/report/station-report/search?${query}`, {
      method: 'POST',
      body: JSON.stringify(filteredParams),
    })
    return data
  } catch (e) {
    message.error('search Station Report error !')
  }
}
export const searchAppUsageReport = async (params: Record<string, any>) => {
  const query = buildQuery(pick(['page', 'pageSize'], params))

  const filteredParams = filterParamsBySupportedTypes(params)
  try {
    if (params.buttonAction === 'Generate Pdf') {
      const response = await fetch(`/report/app-usage-report/generate/pdf`, {
        method: 'POST',
        body: JSON.stringify(filteredParams),
        filename: `AppUsageReport_${getTodayDateString()}.pdf`,
        responseType: 'blob',
      })
    } else if (params.buttonAction === 'Generate Csv') {
      const response = await fetch(`/report/app-usage-report/generate/csv`, {
        method: 'POST',
        body: JSON.stringify(filteredParams),
        filename: `AppUsageReport_${getTodayDateString()}.csv`,
        responseType: 'blob',
        contentType: 'text/csv',
      })
    }

    const { data } = await fetch(`/report/app-usage-report/search?${query}`, {
      method: 'POST',
      body: JSON.stringify(filteredParams),
    })
    return data
  } catch (e) {
    message.error('search App Usage Report error !')
  }
}
export const searchMetaverseReport = async (params: Record<string, any>) => {
  const query = buildQuery(pick(['page', 'pageSize'], params))

  const filteredParams = filterParamsBySupportedTypes(params)
  try {
    if (params.buttonAction === 'Generate Pdf') {
      const response = await fetch(`/report/metaverse-report/generate/pdf`, {
        method: 'POST',
        filename: `MetaverseReport_${getTodayDateString()}.pdf`,
        body: JSON.stringify(filteredParams),
        responseType: 'blob',
      })
    } else if (params.buttonAction === 'Generate Csv') {
      const response = await fetch(`/report/metaverse-report/generate/csv`, {
        method: 'POST',
        body: JSON.stringify(filteredParams),
        filename: `MetaverseReport_${getTodayDateString()}.csv`,
        responseType: 'blob',
        contentType: 'text/csv',
      })
    }

    const { data } = await fetch(`/report/metaverse-report/search?${query}`, {
      method: 'POST',
      body: JSON.stringify(filteredParams),
    })
    return data
  } catch (e) {
    message.error('search metaverse Report error !')
  }
}

export const searchMazeReport = async (params: Record<string, any>) => {
  const queryParam = pick(['page', 'pageSize'], params)
  if (queryParam['pageSize'] === undefined) {
    queryParam['size'] = 10
  } else {
    queryParam['size'] = queryParam['pageSize']
  }
  const query = buildQuery(queryParam)
  const filteredParams = filterParamsBySupportedTypes(params)
  try {
    if (params.buttonAction === 'Generate Pdf') {
      const response = await fetch(`/report/maze-report/generate/pdf`, {
        body: JSON.stringify(filteredParams),
        responseType: 'blob',
        filename: `MazeUsageReport_${getTodayDateString()}.pdf`,
        method: 'POST',
      })
    } else if (params.buttonAction === 'Generate Csv') {
      const response = await fetch(`/report/maze-report/generate/csv`, {
        body: JSON.stringify(filteredParams),
        responseType: 'blob',
        filename: `MazeUsageReport_${getTodayDateString()}.csv`,
        method: 'POST',
        contentType: 'text/csv',
      })
    }

    const { data } = await fetch(`/report/maze-report/search?${query}`, {
      method: 'POST',
      body: JSON.stringify(filteredParams),
    })
    return data
  } catch (e) {
    message.error('search maze Report error !')
  }
}

export const searchUserQueriesReport = async (params: Record<string, any>) => {
  const query = buildQuery(pick(['page', 'pageSize'], params))

  const filteredParams = filterParamsBySupportedTypes(params)
  try {
    if (params.buttonAction === 'Generate Pdf') {
      const response = await fetch(`/report/user-queries-report/generate/pdf`, {
        method: 'POST',
        filename: `UserQueriesReport_${getTodayDateString()}.pdf`,
        body: JSON.stringify(filteredParams),
        responseType: 'blob',
      })
    } else if (params.buttonAction === 'Generate Csv') {
      const response = await fetch(`/report/user-queries-report/generate/csv`, {
        method: 'POST',
        body: JSON.stringify(filteredParams),
        filename: `UserQueriesReport_${getTodayDateString()}.csv`,
        responseType: 'blob',
        contentType: 'text/csv',
      })
    }

    const { data } = await fetch(
      `/report/user-queries-report/search?${query}`,
      {
        method: 'POST',
        body: JSON.stringify(filteredParams),
      }
    )
    return data
  } catch (e) {
    message.error('search User Queries Report error !')
  }
}
export const searchUserProfileChangeReport = async (
  params: Record<string, any>
) => {
  const query = buildQuery(pick(['page', 'pageSize'], params))

  const filteredParams = filterParamsBySupportedTypes(params)
  try {
    if (params.buttonAction === 'Generate Pdf') {
      const response = await fetch(
        `/report/profile-change-report/generate/pdf`,
        {
          method: 'POST',
          filename: `ProfileChangeReport_${getTodayDateString()}.pdf`,
          body: JSON.stringify(filteredParams),
          responseType: 'blob',
        }
      )
    } else if (params.buttonAction === 'Generate Csv') {
      const response = await fetch(
        `/report/profile-change-report/generate/csv`,
        {
          method: 'POST',
          body: JSON.stringify(filteredParams),
          filename: `ProfileChangeReport_${getTodayDateString()}.csv`,
          responseType: 'blob',
          contentType: 'text/csv',
        }
      )
    }

    const { data } = await fetch(
      `/report/profile-change-report/search?${query}`,
      {
        method: 'POST',
        body: JSON.stringify(filteredParams),
      }
    )
    return data
  } catch (e) {
    message.error('search User Profile Change Report error !')
  }
}

export const searchDeleteUserReport = async (params: Record<string, any>) => {
  const query = buildQuery(pick(['page', 'pageSize'], params))

  const filteredParams = filterParamsBySupportedTypes(params)
  try {
    if (params.buttonAction === 'Generate Pdf') {
      const response = await fetch(
        `/report/user-delete-account-report/generate/pdf`,
        {
          method: 'POST',
          body: JSON.stringify(filteredParams),
          filename: `DeleteUserAccount_${getTodayDateString()}.pdf`,
          responseType: 'blob',
        }
      )
    } else if (params.buttonAction === 'Generate Csv') {
      const response = await fetch(
        `/report/user-delete-account-report/generate/csv`,
        {
          method: 'POST',
          body: JSON.stringify(filteredParams),
          responseType: 'blob',
          filename: `DeleteUserAccount_${getTodayDateString()}.csv`,
          contentType: 'text/csv',
        }
      )
    }

    const { data } = await fetch(
      `/report/user-delete-account-report/search?${query}`,
      {
        method: 'POST',
        body: JSON.stringify(filteredParams),
      }
    )
    return data.deleteUsers
  } catch (e) {
    message.error('search Game Usage Report error !')
  }
}

export const searchRedeemUserReport = async (params: Record<string, any>) => {
  const query = buildQuery(pick(['page', 'size'], params))
  const filteredParams = filterParamsBySupportedTypes({
    ...params,
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
  })

  try {
    if (params.buttonAction === 'Generate Pdf') {
      await fetch(`/report/redeem-user-report/generate/pdf`, {
        method: 'POST',
        body: JSON.stringify(filteredParams),
        filename: `RedeemUserAccount_${getTodayDateString()}.pdf`,
        responseType: 'blob',
      })
    } else if (params.buttonAction === 'Generate Csv') {
      await fetch(`/report/redeem-user-report/generate/csv`, {
        method: 'POST',
        body: JSON.stringify(filteredParams),
        responseType: 'blob',
        filename: `RedeemUserAccount_${getTodayDateString()}.csv`,
        contentType: 'text/csv',
      })
    }

    const { data } = await fetch(`/report/redeem-user-report/search?${query}`, {
      method: 'POST',
      body: JSON.stringify(filteredParams),
    })
    return data
  } catch (e) {
    message.error('search Redeem User error !')
  }
}
export const searchStatsDeleteUserReport = async (
  params: Record<string, any>
) => {
  const query = buildQuery(pick(['page', 'pageSize'], params))

  const filteredParams = filterParamsBySupportedTypes(params)
  try {
    const { data } = await fetch(
      `/report/user-delete-account-report/search?${query}`,
      {
        method: 'POST',
        body: JSON.stringify(filteredParams),
      }
    )
    return data
  } catch (e) {
    message.error('search Game Usage Report error !')
  }
}

export const searchGameReport = async (params: Record<string, any>) => {
  const query = buildQuery(pick(['page', 'pageSize'], params))

  const filteredParams = filterParamsBySupportedTypes(params)
  try {
    if (params.buttonAction === 'Generate Pdf') {
      const response = await fetch(`/report/game-usage-report/generate/pdf`, {
        method: 'POST',
        body: JSON.stringify(filteredParams),
        filename: `GameUsageReport_${getTodayDateString()}.pdf`,
        responseType: 'blob',
      })
    } else if (params.buttonAction === 'Generate Csv') {
      const response = await fetch(`/report/game-usage-report/generate/csv`, {
        method: 'POST',
        body: JSON.stringify(filteredParams),
        responseType: 'blob',
        filename: `GameUsageReport_${getTodayDateString()}.csv`,
        contentType: 'text/csv',
      })
    }

    const { data } = await fetch(`/report/game-usage-report/search?${query}`, {
      method: 'POST',
      body: JSON.stringify(filteredParams),
    })
    return data
  } catch (e) {
    message.error('search Game Usage Report error !')
  }
}

export const getListOfGames = async () => {
  try {
    const { data } = await fetch('/report/game-usage-report/games')
    return data
  } catch (e) {
    message.error('Get List of Gams error!')
  }
}
export const searchChatUsageReport = async (params: Record<string, any>) => {
  const query = buildQuery(pick(['page', 'pageSize'], params))

  const filteredParams = filterParamsBySupportedTypes(params)
  try {
    if (params.buttonAction === 'Generate Pdf') {
      const response = await fetch(`/report/chat-usage-report/generate/pdf`, {
        method: 'POST',
        body: JSON.stringify(filteredParams),
        filename: `ChatUsageReport_${getTodayDateString()}.pdf`,
        responseType: 'blob',
      })
    } else if (params.buttonAction === 'Generate Csv') {
      const response = await fetch(`/report/chat-usage-report/generate/csv`, {
        method: 'POST',
        body: JSON.stringify(filteredParams),
        filename: `ChatUsageReport_${getTodayDateString()}.csv`,
        responseType: 'blob',
        contentType: 'text/csv',
      })
    }

    const { data } = await fetch(`/report/chat-usage-report/search?${query}`, {
      method: 'POST',
      body: JSON.stringify(filteredParams),
    })
    return data
  } catch (e) {
    message.error('search Chat Usage Report error !')
  }
}

export const searchMarketplaceUsageReport = async (
  params: Record<string, any>
) => {
  const query = buildQuery(pick(['page', 'pageSize'], params))

  const filteredParams = filterParamsBySupportedTypes(params)
  try {
    if (params.buttonAction === 'Generate Pdf') {
      const response = await fetch(
        `/report/marketplace-usage-report/generate/pdf`,
        {
          method: 'POST',
          body: JSON.stringify(filteredParams),
          filename: `MarketplaceUsageReport_${getTodayDateString()}.pdf`,
          responseType: 'blob',
        }
      )
    } else if (params.buttonAction === 'Generate Csv') {
      const response = await fetch(
        `/report/marketplace-usage-report/generate/csv`,
        {
          method: 'POST',
          body: JSON.stringify(filteredParams),
          filename: `MarketplaceUsageReport_${getTodayDateString()}.csv`,
          responseType: 'blob',
          contentType: 'text/csv',
        }
      )
    }

    const { data } = await fetch(
      `/report/marketplace-usage-report/search?${query}`,
      {
        method: 'POST',
        body: JSON.stringify(filteredParams),
      }
    )
    return data
  } catch (e) {
    message.error('search Marketplace Usage Report error !')
  }
}

export const searchUserprofileUsageReport = async (
  params: Record<string, any>
) => {
  const query = buildQuery(pick(['page', 'pageSize'], params))

  const filteredParams = filterParamsBySupportedTypes(params)
  try {
    if (params.buttonAction === 'Generate Pdf') {
      const response = await fetch(
        `/report/user-profile-edit-usage-report/generate/pdf`,
        {
          method: 'POST',
          body: JSON.stringify(filteredParams),
          filename: `UserprofileUsageReport_${getTodayDateString()}.pdf`,
          responseType: 'blob',
        }
      )
    } else if (params.buttonAction === 'Generate Csv') {
      const response = await fetch(
        `/report/user-profile-edit-usage-report/generate/csv`,
        {
          method: 'POST',
          body: JSON.stringify(filteredParams),
          filename: `UserprofileUsageReport_${getTodayDateString()}.csv`,
          responseType: 'blob',
          contentType: 'text/csv',
        }
      )
    }

    const { data } = await fetch(
      `/report/user-profile-edit-usage-report/search?${query}`,
      {
        method: 'POST',
        body: JSON.stringify(filteredParams),
      }
    )
    return data
  } catch (e) {
    message.error('search User Profile Usage Report error !')
  }
}

export const searchSelectAvatarUsageReport = async (
  params: Record<string, any>
) => {
  const query = buildQuery(pick(['page', 'pageSize'], params))

  const filteredParams = filterParamsBySupportedTypes(params)
  try {
    if (params.buttonAction === 'Generate Pdf') {
      const response = await fetch(
        `/report/avatar-usage-report/select/generate/pdf`,
        {
          method: 'POST',
          body: JSON.stringify(filteredParams),
          filename: `SelectAvatarUsageReport_${getTodayDateString()}.pdf`,
          responseType: 'blob',
        }
      )
    } else if (params.buttonAction === 'Generate Csv') {
      const response = await fetch(
        `/report/avatar-usage-report/select/generate/csv`,
        {
          method: 'POST',
          body: JSON.stringify(filteredParams),
          filename: `SelectAvatarUsageReport_${getTodayDateString()}.csv`,
          responseType: 'blob',
          contentType: 'text/csv',
        }
      )
    }

    const { data } = await fetch(
      `/report/avatar-usage-report/select/search?${query}`,
      {
        method: 'POST',
        body: JSON.stringify(filteredParams),
      }
    )
    return data
  } catch (e) {
    message.error('search Select Avatar Usage Report error !')
  }
}

export const searchCreateAvatarUsageReport = async (
  params: Record<string, any>
) => {
  const query = buildQuery(pick(['page', 'pageSize'], params))

  const filteredParams = filterParamsBySupportedTypes(params)
  try {
    if (params.buttonAction === 'Generate Pdf') {
      const response = await fetch(
        `/report/avatar-usage-report/create/generate/pdf`,
        {
          method: 'POST',
          body: JSON.stringify(filteredParams),
          filename: `CreateAvatarUsageReport_${getTodayDateString()}.pdf`,
          responseType: 'blob',
        }
      )
    } else if (params.buttonAction === 'Generate Csv') {
      const response = await fetch(
        `/report/avatar-usage-report/create/generate/csv`,
        {
          method: 'POST',
          body: JSON.stringify(filteredParams),
          filename: `CreateAvatarUsageReport_${getTodayDateString()}.csv`,
          responseType: 'blob',
          contentType: 'text/csv',
        }
      )
    }

    const { data } = await fetch(
      `/report/avatar-usage-report/create/search?${query}`,
      {
        method: 'POST',
        body: JSON.stringify(filteredParams),
      }
    )
    return data
  } catch (e) {
    message.error('search Create Avatar Usage Report error !')
  }
}

export const searchSocialUsageReport = async (params: Record<string, any>) => {
  const query = buildQuery(pick(['page', 'pageSize'], params))

  const filteredParams = filterParamsBySupportedTypes(params)
  try {
    if (params.buttonAction === 'Generate Pdf') {
      const response = await fetch(`/report/social-usage-report/generate/pdf`, {
        method: 'POST',
        body: JSON.stringify(filteredParams),
        filename: `SocialUsageReport_${getTodayDateString()}.pdf`,
        responseType: 'blob',
      })
    } else if (params.buttonAction === 'Generate Csv') {
      const response = await fetch(`/report/social-usage-report/generate/csv`, {
        method: 'POST',
        body: JSON.stringify(filteredParams),
        filename: `SocialUsageReport_${getTodayDateString()}.csv`,
        responseType: 'blob',
        contentType: 'text/csv',
      })
    }

    const { data } = await fetch(
      `/report/social-usage-report/search?${query}`,
      {
        method: 'POST',
        body: JSON.stringify(filteredParams),
      }
    )
    return data
  } catch (e) {
    message.error('search Social Usage Report error !')
  }
}
export const searchTreasureHunterReport = async (
  params: Record<string, any>
) => {
  const query = buildQuery(pick(['page', 'pageSize'], params))

  const filteredParams = filterParamsBySupportedTypes(params)
  try {
    if (params.buttonAction === 'Generate Pdf') {
      const response = await fetch(
        `/report/treasure-hunter-report/generate/pdf`,
        {
          method: 'POST',
          body: JSON.stringify(filteredParams),
          filename: `TreasureHunterReport_${getTodayDateString()}.pdf`,
          responseType: 'blob',
        }
      )
    } else if (params.buttonAction === 'Generate Csv') {
      const response = await fetch(
        `/report/treasure-hunter-report/generate/csv`,
        {
          method: 'POST',
          body: JSON.stringify(filteredParams),
          responseType: 'blob',
          filename: `TreasureHunterReport_${getTodayDateString()}.csv`,
          contentType: 'text/csv',
        }
      )
    }

    const { data } = await fetch(
      `/report/treasure-hunter-report/search?${query}`,
      {
        method: 'POST',
        body: JSON.stringify(filteredParams),
      }
    )
    return data
  } catch (e) {
    message.error('search Treasure Hunter Report error !')
  }
}
export const searchSurvivorZoneReport = async (params: Record<string, any>) => {
  params.gameCategory = ['EXSCAPEGUYS']
  const query = buildQuery(pick(['page', 'pageSize'], params))

  const filteredParams = filterParamsBySupportedTypes(params)
  try {
    if (params.buttonAction === 'Generate Pdf') {
      const response = await fetch(`/report/game-usage-report/generate/pdf`, {
        method: 'POST',
        body: JSON.stringify(filteredParams),
        filename: `SurvivorZoneReport_${getTodayDateString()}.pdf`,
        responseType: 'blob',
      })
    } else if (params.buttonAction === 'Generate Csv') {
      const response = await fetch(`/report/game-usage-report/generate/csv`, {
        method: 'POST',
        body: JSON.stringify(filteredParams),
        responseType: 'blob',
        filename: `SurvivorZoneReport_${getTodayDateString()}.csv`,
        contentType: 'text/csv',
      })
    }

    const { data } = await fetch(`/report/game-usage-report/search?${query}`, {
      method: 'POST',
      body: JSON.stringify(filteredParams),
    })
    return data
  } catch (e) {
    message.error('search Survivor Zone Report error !')
  }
}
export const getGameUsageChart = async (query: Record<string, any>) => {
  const filteredParams = filterParamsBySupportedTypes(query)
  const groupBy = 'hourly'

  try {
    const { data } = await fetch(
      `/report/game-usage-report/hourly-usage-chart?groupBy=${groupBy}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(filteredParams),
      }
    )

    return data
  } catch (e) {
    console.log('Error fetching Game Usage Chart!')
  }
}

export const getGameUsageReportMetrics = async (query: Record<string, any>) => {
  const filteredParams = filterParamsBySupportedTypes(query)

  try {
    const { data } = await fetch('/report/game-usage-report/hourly-metrics', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(filteredParams),
    })

    return data
  } catch (e) {
    console.error('Error fetching Game Usage Report Metrics!')
  }
}
export const removeEmptyAttributes = (
  obj: Record<string, any>
): Record<string, any> => {
  if (!obj || typeof obj !== 'object') {
    return {}
  }

  const newObj: Record<string, any> = {}

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key]
      if (
        value !== null &&
        value !== undefined &&
        value !== '' &&
        !(Array.isArray(value) && value.length === 0) &&
        !(Array.isArray(value) && value.every((item) => item === null)) &&
        !(
          typeof value === 'object' &&
          value !== null &&
          Object.keys(value).length === 0
        )
      ) {
        newObj[key] = value
      }
    }
  }

  return newObj
}
export const getAppUsageChart = async (query: Record<string, any>) => {
  const filteredParams = filterParamsBySupportedTypes(query)

  const groupBy = 'hourly'

  try {
    const { data } = await fetch(
      `/report/app-usage-report/hourly-usage-chart?groupBy=${groupBy}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(filteredParams),
      }
    )

    return data
  } catch (e) {
    console.log('Error fetching App Usage Chart!')
  }
}
export const getAppUsageReportMetrics = async (query: Record<string, any>) => {
  const filteredParams = filterParamsBySupportedTypes(query)

  try {
    const { data } = await fetch('/report/app-usage-report/hourly-metrics', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(filteredParams),
    })

    return data
  } catch (e) {
    console.error('Error fetching App Usage Report Metrics!')
  }
}

const buildParams = (baseParams, providerCountry) => {
  const params = new URLSearchParams(baseParams)
  if (providerCountry && providerCountry !== null) {
    params.append('providerCountry', providerCountry)
  }
  return params.toString()
}

export const getPostOverview = async (providerCountry, fromDate, toDate) => {
  try {
    const params = buildParams({ from: fromDate, to: toDate }, providerCountry)
    const { data } = await fetch(`/report/social/post/overview?${params}`)
    return data
  } catch (e) {
    console.error('Error Fetching Post Overview!')
  }
}

export const getChatOverview = async (providerCountry, fromDate, toDate) => {
  try {
    const params = buildParams({ from: fromDate, to: toDate }, providerCountry)
    const { data } = await fetch(`/report/social/chat/overview?${params}`)
    return data
  } catch (e) {
    console.error('Error Fetching Chat Overview!')
  }
}

export const top10Follwer = async (
  providerCountry,
  fromDate,
  toDate,
  userType
) => {
  try {
    const params = buildParams(
      { from: fromDate, to: toDate, userTpe: userType },
      providerCountry
    )
    const { data } = await fetch(`/report/social/post/top10Follower?${params}`)
    return data
  } catch (e) {
    console.error('Error Fetching Top Followers!')
  }
}

export const top10Engagement = async (
  providerCountry,
  fromDate,
  toDate,
  type
) => {
  try {
    const params = buildParams(
      { from: fromDate, to: toDate, type },
      providerCountry
    )
    const { data } = await fetch(
      `/report/social/post/top10Engagement?${params}`
    )
    return data
  } catch (e) {
    console.error('Error Fetching Top Engagement!')
  }
}

export const top10Impression = async (
  providerCountry,
  fromDate,
  toDate,
  type
) => {
  try {
    const params = buildParams(
      { from: fromDate, to: toDate, type },
      providerCountry
    )
    const { data } = await fetch(
      `/report/social/post/top10Impression?${params}`
    )
    return data
  } catch (e) {
    console.error('Error Fetching Top Impression!')
  }
}

export const getSocialHeatMap = async (
  providerCountry,
  fromDate,
  toDate,
  type
) => {
  try {
    const params = buildParams(
      { from: fromDate, to: toDate, type },
      providerCountry
    )
    const { data } = await fetch(`/report/social/post/heatmap?${params}`)
    return data
  } catch (e) {
    console.error('Error Fetching Social Heatmap!')
  }
}

export const getSocialDevice = async (providerCountry, fromDate, toDate) => {
  try {
    const params = buildParams({ from: fromDate, to: toDate }, providerCountry)
    const { data } = await fetch(`/report/social/post/device?${params}`)
    return data
  } catch (e) {
    console.error('Error Fetching Social Device!')
  }
}

export const getSocialGender = async (providerCountry, fromDate, toDate) => {
  try {
    const params = buildParams({ from: fromDate, to: toDate }, providerCountry)
    const { data } = await fetch(`/report/social/post/gender?${params}`)
    return data
  } catch (e) {
    console.error('Error Fetching Social Gender!')
  }
}

export const searchAvatarReport = async (params: Record<string, any>) => {
  const query = buildQuery(pick(['page', 'pageSize'], params))

  const filteredParams = filterParamsBySupportedTypes(params)
  try {
    if (params.buttonAction === 'Generate Pdf') {
      const response = await fetch(`/report/avatar-report/generate/pdf`, {
        method: 'POST',
        body: JSON.stringify(filteredParams),
        filename: `AvatarReport_${getTodayDateString()}.pdf`,
        responseType: 'blob',
      })
    } else if (params.buttonAction === 'Generate Csv') {
      const response = await fetch(`/report/avatar-report/generate/csv`, {
        method: 'POST',
        body: JSON.stringify(filteredParams),
        responseType: 'blob',
        filename: `AvatarReport_${getTodayDateString()}.csv`,
        contentType: 'text/csv',
      })
    }

    const { data } = await fetch(`/report/avatar-report/search?${query}`, {
      method: 'POST',
      body: JSON.stringify(filteredParams),
    })
    return data
  } catch (e) {
    message.error('search Avatar Report error !')
  }
}
export const getAvatarNames = async () => {
  try {
    const { data } = await fetch('/report/avatar-report/avatars')
    return data
  } catch (e) {
    message.error('Get Avatar Names error!')
  }
}
export const getAvatarSelectionReportMetrics = async (
  query: Record<string, any>
) => {
  const filteredParams = filterParamsBySupportedTypes(query)

  try {
    const { data } = await fetch('/report/avatar-report/metrics', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(filteredParams),
    })

    return data
  } catch (e) {
    console.error('Error fetching Avatar Report Metrics!')
  }
}
export const getAvatarDashboardMetrics = async (query: Record<string, any>) => {
  const filteredParams = filterParamsBySupportedTypes(query)

  try {
    const { data } = await fetch('/report/avatar-dashboard/metrics', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(filteredParams),
    })

    return data
  } catch (e) {
    console.error('Error fetching top10 Avatar Dashboard Metrics!')
  }
}

export const fetchMarketPlaces = async () => {
  try {
    const { data } = await fetch('/report/marketplace')

    return data
  } catch (e) {
    console.error('Error Fetch MarketPlaces!')
  }
}
export const getTop10AvatarMetrics = async (query: Record<string, any>) => {
  const filteredParams = filterParamsBySupportedTypes(query)

  try {
    const { data } = await fetch('/report/avatar-dashboard/top10-avatars', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(filteredParams),
    })

    return data
  } catch (e) {
    console.error('Error fetching top10 Custom Avatar Dashboard Metrics!')
  }
}
export const getTop10CustomAvatarMetrics = async (
  query: Record<string, any>
) => {
  const filteredParams = filterParamsBySupportedTypes(query)

  try {
    const { data } = await fetch(
      '/report/avatar-dashboard/top10-custom-avatars',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(filteredParams),
      }
    )

    return data
  } catch (e) {
    console.error('Error fetching Avatar Dashboard Metrics!')
  }
}
export const getAvatarDashboardChart = async (query: Record<string, any>) => {
  const filteredParams = filterParamsBySupportedTypes(query)

  try {
    const { data } = await fetch('/report/avatar-dashboard/chart', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(filteredParams),
    })

    return data
  } catch (e) {
    console.error('Error fetching Avatar Dashboard Chart!')
  }
}
export const searchUserActivityLog = async (params: Record<string, any>) => {
  const query = buildQuery(pick(['page', 'pageSize'], params))

  const filteredParams = filterParamsBySupportedTypes(params)
  try {
    const { data } = await fetch(`/report/user/activity-log?${query}`, {
      method: 'POST',
      body: JSON.stringify(filteredParams),
    })
    return data
  } catch (e) {
    message.error('search User Activity Log error !')
  }
}
export const searchUserJourneyReport = async (params: Record<string, any>) => {
  const query = buildQuery(pick(['page', 'pageSize'], params))

  const filteredParams = filterParamsBySupportedTypes(params)
  try {
    if (params.buttonAction === 'Generate Pdf') {
      const response = await fetch(`/report/user-journey-report/generate/pdf`, {
        method: 'POST',
        body: JSON.stringify(filteredParams),
        filename: `UserJourneyReport_${getTodayDateString()}.pdf`,
        responseType: 'blob',
      })
    } else if (params.buttonAction === 'Generate Csv') {
      const response = await fetch(`/report/user-journey-report/generate/csv`, {
        method: 'POST',
        body: JSON.stringify(filteredParams),
        filename: `UserJourneyReport_${getTodayDateString()}.csv`,
        responseType: 'blob',
        contentType: 'text/csv',
      })
    }

    const { data } = await fetch(
      `/report/user-journey-report/search?${query}`,
      {
        method: 'POST',
        body: JSON.stringify(filteredParams),
      }
    )
    return data
  } catch (e) {
    message.error('search User Journey Report error !')
  }
}
export const getUserJourneyReportMetrics = async (
  query: Record<string, any>
) => {
  const filteredParams = filterParamsBySupportedTypes(query)

  try {
    const { data } = await fetch('/report/user-journey-report/metrics', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(filteredParams),
    })

    return data
  } catch (e) {
    console.error('Error fetching UserJoureny Metrics!')
  }
}
export const searchAdsReport = async (params: Record<string, any>) => {
  const query = buildQuery(pick(['page', 'pageSize'], params))

  const filteredParams = filterParamsBySupportedTypes(params)
  try {
    if (params.buttonAction === 'Generate Pdf') {
      const response = await fetch(`/report/ads-report/generate/pdf`, {
        method: 'POST',
        body: JSON.stringify(filteredParams),
        filename: `AdsReport_${getTodayDateString()}.pdf`,
        responseType: 'blob',
      })
    } else if (params.buttonAction === 'Generate Csv') {
      const response = await fetch(`/report/ads-report/generate/csv`, {
        method: 'POST',
        body: JSON.stringify(filteredParams),
        responseType: 'blob',
        filename: `AdsReport_${getTodayDateString()}.csv`,
        contentType: 'text/csv',
      })
    }

    const { data } = await fetch(`/report/ads-report/search?${query}`, {
      method: 'POST',
      body: JSON.stringify(filteredParams),
    })
    return data
  } catch (e) {
    message.error('search Ads Report error !')
  }
}
export const getAllGames = async (type?: string) => {
  try {
    const url = type 
      ? `/report/game-usage-report/all-games?type=${type}` 
      : '/report/game-usage-report/all-games';
    const { data } = await fetch(url)
    return data
  } catch (e) {
    message.error('Get List of Games error!')
  }
}
export const searchTreasureRedeemReport = async (
  params: Record<string, any>
) => {
  const query = buildQuery(pick(['page', 'pageSize'], params))

  const filteredParams = filterParamsBySupportedTypes(params)
  try {
    if (params.buttonAction === 'Generate Pdf') {
      const response = await fetch(
        `/report/treasure-redeem-report/generate/pdf`,
        {
          method: 'POST',
          body: JSON.stringify(filteredParams),
          filename: `TreasureRedeemReport_${getTodayDateString()}.pdf`,
          responseType: 'blob',
        }
      )
    } else if (params.buttonAction === 'Generate Csv') {
      const response = await fetch(
        `/report/treasure-redeem-report/generate/csv`,
        {
          method: 'POST',
          body: JSON.stringify(filteredParams),
          responseType: 'blob',
          filename: `TreasureRedeemReport_${getTodayDateString()}.csv`,
          contentType: 'text/csv',
        }
      )
    }

    const { data } = await fetch(
      `/report/treasure-redeem-report/search?${query}`,
      {
        method: 'POST',
        body: JSON.stringify(filteredParams),
      }
    )
    return data
  } catch (e) {
    message.error('search Treasure Redeem Report error !')
  }
}
export const searchUserPointsReport = async (params: Record<string, any>) => {
  const query = buildQuery(pick(['page', 'pageSize'], params))

  const filteredParams = filterParamsBySupportedTypes(params)
  try {
    if (params.buttonAction === 'Generate Pdf') {
      const response = await fetch(`/report/user-points-report/generate/pdf`, {
        method: 'POST',
        body: JSON.stringify(filteredParams),
        filename: `UserPointsReport_${getTodayDateString()}.pdf`,
        responseType: 'blob',
      })
    } else if (params.buttonAction === 'Generate Csv') {
      const response = await fetch(`/report/user-points-report/generate/csv`, {
        method: 'POST',
        body: JSON.stringify(filteredParams),
        responseType: 'blob',
        filename: `UserPointsReport_${getTodayDateString()}.csv`,
        contentType: 'text/csv',
      })
    }

    const { data } = await fetch(`/report/user-points-report/search?${query}`, {
      method: 'POST',
      body: JSON.stringify(filteredParams),
    })
    return data
  } catch (e) {
    message.error('search User Points Report error !')
  }
}
export const getAvatarReportChart = async (query: Record<string, any>) => {
  const filteredParams = filterParamsBySupportedTypes(query)

  try {
    const { data } = await fetch('/report/avatar-report/chart', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(filteredParams),
    })

    return data
  } catch (e) {
    console.error('Error fetching Avatar Dashboard Chart!')
  }
}

export const getAccessTokenCheaters = async (page = 0, size = 10) => {
  try {
    const { data } = await fetch(
      `/report/game-cheater?page=${page}&pageSize=${size}`
    )
    return data
  } catch (e) {
    console.error('Error Access Token Cheater!')
  }
}

export const getGameAnomaly = async (page = 0, size = 10) => {
  try {
    const { data } = await fetch(
      `/report/game-result-anomaly?page=${page}&pageSize=${size}`
    )
    return data
  } catch (e) {
    console.error('Error Game Anomaly!')
  }
}

export const getTreasureAnomaly = async (page = 0, size = 10) => {
  try {
    const { data } = await fetch(
      `/report/treasure-reward-anomaly?page=${page}&pageSize=${size}`
    )
    return data
  } catch (e) {
    console.error('Error Treasure Anomaly!')
  }
}
